import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { MAIN_SITE_URL } from "@/utils/constants";
import { generateArticleJsonLd, generateBreadcrumbJsonLd } from "@/utils/seo-schema";

const articlePath = "/artykul/kakobuy-opinie";
const articleImage = "https://readdy.ai/api/search-image?query=Person%20hands%20typing%20on%20laptop%20at%20warm%20cream%20desk%20with%20coffee%20cup%2C%20soft%20natural%20morning%20light%20streaming%20in%2C%20reading%20online%20reviews%20concept%2C%20minimalist%20workspace%20photography%2C%20warm%20beige%20and%20sand%20tones%2C%20calm%20focused%20atmosphere%2C%20european%20lifestyle%20editorial%2C%20consumer%20research%20and%20decision%20making&width=1200&height=800&seq=kakobuy-article-opinie&orientation=landscape";

const schemas = [
  generateArticleJsonLd({
    headline: "Kakobuy Opinie — Co Mówią Polscy Użytkownicy?",
    description: "Szczera analiza opinii polskich użytkowników Kakobuy. Sprawdzamy, co kupujący mówią o jakości produktów, obsłudze klienta i całym doświadczeniu zakupowym.",
    datePublished: "2026-07-01",
    dateModified: "2026-07-21",
    imageUrl: articleImage,
    articlePath,
  }),
  generateBreadcrumbJsonLd([
    { name: "Strona główna", path: "/" },
    { name: "Kakobuy Opinie", path: articlePath },
  ]),
];

export default function KakobuyOpinieArticle() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-20">
        <article className="w-full px-4 md:px-8 py-10 md:py-16">
          <div className="max-w-3xl mx-auto">
            <nav className="flex items-center gap-2 text-xs text-foreground-600 mb-8 flex-wrap">
              <Link to="/" className="hover:text-foreground-900 transition-colors cursor-pointer">Strona główna</Link>
              <span className="w-3 h-3 flex items-center justify-center"><i className="ri-arrow-right-s-line text-xs"></i></span>
              <span className="text-foreground-900 font-medium">Kakobuy Opinie</span>
            </nav>

            <header className="mb-10">
              <div className="inline-flex items-center gap-2 bg-secondary-100 px-4 py-1.5 rounded-full mb-4">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-message-2-line text-secondary-500 text-sm"></i></span>
                <span className="text-xs font-semibold text-secondary-700 whitespace-nowrap">Opinie użytkowników</span>
              </div>
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 leading-tight mb-4">
                Kakobuy Opinie — Co Mówią Polscy Użytkownicy?
              </h1>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                Szczera analiza opinii polskich użytkowników Kakobuy. Sprawdzamy, co kupujący mówią o jakości produktów, obsłudze klienta i całym doświadczeniu zakupowym.
              </p>
              <div className="flex items-center gap-3 text-xs text-foreground-600 flex-wrap">
                <span>Ostatnia aktualizacja: Lipiec 2026</span>
                <span className="text-foreground-400">•</span>
                <span>Czas czytania: 10 minut</span>
              </div>
            </header>

            <div className="w-full h-56 md:h-72 rounded-xl overflow-hidden mb-10">
              <img
                src="https://readdy.ai/api/search-image?query=Person%20hands%20typing%20on%20laptop%20at%20warm%20cream%20desk%20with%20coffee%20cup%2C%20soft%20natural%20morning%20light%20streaming%20in%2C%20reading%20online%20reviews%20concept%2C%20minimalist%20workspace%20photography%2C%20warm%20beige%20and%20sand%20tones%2C%20calm%20focused%20atmosphere%2C%20european%20lifestyle%20editorial%2C%20consumer%20research%20and%20decision%20making&width=1200&height=800&seq=kakobuy-article-opinie&orientation=landscape"
                alt="Kakobuy Opinie — Recenzje użytkowników"
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="prose-custom text-sm md:text-base text-foreground-800 leading-relaxed space-y-8">
              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">Ogólny obraz — Kakobuy w oczach Polaków</h2>
                <p className="mb-3">Opinie polskich użytkowników na temat Kakobuy są w przeważającej większości pozytywne. Platforma zdobyła uznanie przede wszystkim za ogromny wybór produktów, konkurencyjne ceny oraz wygodę korzystania z gotowych arkuszy (spreadsheetów).</p>
                <p className="mb-3">Wielu kupujących podkreśla, że Kakobuy Spreadsheet znacząco ułatwia znalezienie interesujących produktów — zamiast ręcznie przeszukiwać platformę, wystarczy otworzyć odpowiednią kategorię w arkuszu i przeglądać sprawdzone linki.</p>
                <p>Negatywne opinie najczęściej dotyczą czasu oczekiwania na dostawę, co jest jednak standardowym wyzwaniem przy międzynarodowych zakupach. Użytkownicy doceniają natomiast możliwość śledzenia przesyłki i ochronę kupujących.</p>
              </section>

              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">Co Polacy chwalą w Kakobuy?</h2>
                <div className="space-y-3">
                  {[
                    { t: "Ogromny wybór produktów", d: "Użytkownicy podkreślają, że na Kakobuy można znaleźć praktycznie wszystko — od butów sportowych po akcesoria elektroniczne i kosmetyki.", stars: 5 },
                    { t: "Konkurencyjne ceny", d: "Ceny produktów są znacznie niższe niż w polskich sklepach, co przyciąga kupujących szukających okazji.", stars: 5 },
                    { t: "Kakobuy Spreadsheet", d: "Arkusz z posegregowanymi linkami to według użytkowników największe ułatwienie — oszczędza godziny szukania.", stars: 5 },
                    { t: "System ochrony kupujących", d: "Możliwość otwarcia sporu i odzyskania pieniędzy w przypadku problemów daje poczucie bezpieczeństwa.", stars: 4 },
                    { t: "Zdjęcia QC", d: "Możliwość zobaczenia rzeczywistego wyglądu produktu przed wysyłką jest bardzo ceniona przez kupujących.", stars: 5 },
                    { t: "Obsługa klienta", d: "Większość użytkowników chwali szybką i pomocną obsługę klienta, choć zdarzają się pojedyncze negatywne opinie.", stars: 4 },
                  ].map((item) => (
                    <div key={item.t} className="bg-background-100 rounded-lg p-4 border border-background-200/70">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm font-semibold text-foreground-900">{item.t}</h3>
                        <span className="text-xs text-secondary-500">{'★'.repeat(item.stars)}{'☆'.repeat(5 - item.stars)}</span>
                      </div>
                      <p className="text-xs text-foreground-600">{item.d}</p>
                    </div>
                  ))}
                </div>
              </section>

              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">Na co skarżą się użytkownicy?</h2>
                <p className="mb-3">Mimo ogólnie pozytywnego odbioru, polscy użytkownicy Kakobuy wskazują również kilka obszarów do poprawy. Oto najczęściej wymieniane zastrzeżenia:</p>
                <div className="space-y-2">
                  {[
                    "Długi czas dostawy — szczególnie przy wyborze ekonomicznych linii wysyłkowych",
                    "Dodatkowe opłaty celne — VAT i cło przy zamówieniach spoza UE",
                    "Jakość niektórych produktów — zdarzają się rozbieżności między zdjęciami a rzeczywistością (stąd znaczenie zdjęć QC)",
                    "Bariera językowa — nie wszystkie opisy produktów są dostępne po polsku",
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 bg-accent-100/50 rounded-lg border border-accent-200/60">
                      <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5"><i className="ri-error-warning-line text-accent-500 text-sm"></i></span>
                      <span className="text-sm text-foreground-700">{item}</span>
                    </div>
                  ))}
                </div>
              </section>

              <section className="bg-gradient-to-r from-accent-100/60 to-primary-100/60 rounded-2xl p-6 md:p-8 text-center border border-accent-200/40">
                <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-3">Dołącz do społeczności zadowolonych kupujących</h2>
                <p className="text-sm text-foreground-700 mb-5 max-w-md mx-auto">Skorzystaj z Kakobuy Spreadsheet i przekonaj się sam, dlaczego Polacy tak chwalą tę platformę.</p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <a href={MAIN_SITE_URL} target="_blank" rel="noopener nofollow" className="w-full sm:w-auto bg-accent-500 text-background-50 font-semibold px-8 py-3 rounded-full hover:bg-accent-600 transition-all text-sm whitespace-nowrap cursor-pointer">Otwórz Kakobuy Spreadsheet</a>
                  <Link to="/" className="w-full sm:w-auto bg-background-50 text-foreground-900 font-semibold px-8 py-3 rounded-full border border-background-300/60 hover:bg-background-100 transition-all text-sm whitespace-nowrap cursor-pointer">Powrót na stronę główną</Link>
                </div>
              </section>
            </div>
          </div>
        </article>
      </main>
      <Footer />
      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json" dangerouslySetInnerHTML={{ __html: s }} />
      ))}
    </div>
  );
}